import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatAED, formatDate } from "../lib/format";

export default function QuarterlyReview() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = () => api.get("/quarterly-reviews").then((r) => setReviews(r.data));
  useEffect(() => {
    document.title = "Quarterly Review · The Synergy Table";
    load();
  }, []);

  return (
    <div className="space-y-10" data-testid="quarterly-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="overline">Report</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-2 tracking-tight">Quarterly Review</h1>
          <p className="text-[#9A9590] mt-3 max-w-xl">A quarter on one page — what we opened, what we closed, who contributed.</p>
        </div>
        {user.role === "admin" && (
          <button onClick={() => setShowNew(true)} className="btn-gold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm self-start" data-testid="new-qr">
            <Plus size={16} /> New review
          </button>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className="ts-card p-12 text-center text-[#9A9590]">No reviews yet. {user.role === "admin" && "Create the first one."}</div>
      ) : (
        <div className="space-y-12">
          {reviews.map((qr) => (
            <article key={qr.id} className="ts-card p-8 sm:p-12 grain-overlay relative">
              <div className="overline">{qr.quarter} · {formatDate(qr.created_at)}</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#F0EDE8] mt-3">A quarter at the table</h2>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <div className="overline">Opportunities opened</div>
                  <div className="font-serif text-5xl text-[#C9A84C] mt-2">{qr.opportunities_opened}</div>
                </div>
                <div>
                  <div className="overline">Deals won</div>
                  <div className="font-serif text-5xl text-[#C9A84C] mt-2">{qr.deals_won}</div>
                </div>
                <div>
                  <div className="overline">Total estimated value</div>
                  <div className="font-serif text-3xl text-[#C9A84C] mt-2">{formatAED(qr.total_estimated_value)}</div>
                </div>
              </div>

              {qr.top_contributor && (
                <div className="mt-10">
                  <div className="overline">Top contributor</div>
                  <div className="font-serif text-2xl text-[#F0EDE8] mt-2">{qr.top_contributor.name} <span className="text-[#C9A84C]">· {qr.top_contributor.sector}</span></div>
                  <div className="text-sm text-[#9A9590] mt-1">
                    {qr.top_contributor.opportunities_posted} opportunities · {qr.top_contributor.deals_participated} deals · score {qr.top_contributor.score}
                  </div>
                </div>
              )}

              <div className="mt-10">
                <div className="overline mb-4">Contribution scoreboard</div>
                <ul className="space-y-2">
                  {(qr.contribution_scores || []).map((s, i) => (
                    <li key={s.member_id} className="flex items-center justify-between border-b border-[#C9A84C]/10 py-2">
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-[#9A9590] w-6">#{i + 1}</span>
                        <span className="text-sm text-[#F0EDE8]">{s.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-[#C9A84C]">{s.sector}</span>
                      </div>
                      <div className="text-xs text-[#9A9590]">
                        {s.opportunities_posted} opps · {s.deals_participated} deals · <span className="text-[#C9A84C]">{s.score}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {qr.discussion_notes && (
                <div className="mt-8">
                  <div className="overline mb-2">Discussion notes</div>
                  <p className="text-[#F0EDE8] whitespace-pre-line leading-relaxed">{qr.discussion_notes}</p>
                </div>
              )}
              {qr.decisions?.length > 0 && (
                <div className="mt-6">
                  <div className="overline mb-2">Decisions</div>
                  <ul className="list-disc list-inside text-[#F0EDE8] space-y-1">
                    {qr.decisions.map((d) => <li key={d}>{d}</li>)}
                  </ul>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {showNew && <NewQRModal onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}

function NewQRModal({ onClose, onCreated }) {
  const [f, setF] = useState({ quarter: "", discussion_notes: "", decisions_text: "" });
  const submit = async (e) => {
    e.preventDefault();
    const decisions = f.decisions_text.split("\n").map(s=>s.trim()).filter(Boolean);
    await api.post("/quarterly-reviews", { quarter: f.quarter, discussion_notes: f.discussion_notes, decisions });
    onClose(); onCreated();
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ts-card p-7 max-w-lg w-full" onClick={(e)=>e.stopPropagation()}>
        <div className="overline">New review</div>
        <h2 className="font-serif text-2xl text-[#F0EDE8] mt-2 mb-6">Close the quarter</h2>
        <form onSubmit={submit} className="space-y-5">
          <div><label className="overline">Quarter label</label><input required className="ts-input" placeholder="Q1 2026" value={f.quarter} onChange={(e)=>setF({...f,quarter:e.target.value})}/></div>
          <div><label className="overline">Discussion notes</label><textarea className="ts-input min-h-[100px] py-2" value={f.discussion_notes} onChange={(e)=>setF({...f,discussion_notes:e.target.value})}/></div>
          <div><label className="overline">Decisions <span className="normal-case tracking-normal text-[#9A9590]">(one per line)</span></label><textarea className="ts-input min-h-[100px] py-2" value={f.decisions_text} onChange={(e)=>setF({...f,decisions_text:e.target.value})}/></div>
          <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={onClose} className="text-sm text-[#9A9590]">Cancel</button><button type="submit" className="btn-gold rounded-md px-5 py-2.5 text-sm">Generate</button></div>
        </form>
      </div>
    </div>
  );
}
